/** Waitlist matching, offers, first-accept-wins and expiry. Pure; services persist the outcome. */
import { MINUTE, HOUR, dayKeyOf, minuteOfDay, toIso, toMs } from './time';
import type { Daypart, DayKey, Id, IsoUtc, Slot, WaitlistEntry, WaitlistOffer } from './types';

export const OFFER_MINUTES = 30;
/** Offers are not made (and expire) once the slot is this close. */
export const OFFER_CUTOFF_HOURS = 2;

/** Morning < 12:00, noon 12:00-16:59, evening from 17:00. */
export function daypartOf(startMs: number, tz: string): Exclude<Daypart, 'any'> {
  const m = minuteOfDay(startMs, tz);
  return m < 12 * 60 ? 'morning' : m < 17 * 60 ? 'noon' : 'evening';
}

/** Does the entry accept this opened slot? (Service duration fit is checked by canBook per entry.) */
export function entryMatchesSlot(entry: WaitlistEntry, slot: Slot, tz: string): boolean {
  if (entry.status !== 'waiting') return false;
  const startMs = toMs(slot.start);
  if (!entry.days.includes(dayKeyOf(startMs, tz))) return false;
  if (entry.daypart !== 'any' && entry.daypart !== daypartOf(startMs, tz)) return false;
  return entry.staffId === undefined || entry.staffId === slot.staffId;
}

/** Waiting entries for a slot, oldest request first. */
export function matchWaitlist(entries: WaitlistEntry[], slot: Slot, tz: string): WaitlistEntry[] {
  return entries.filter((e) => entryMatchesSlot(e, slot, tz)).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/** Offer lifetime: 30 minutes, but never later than 2 hours before the slot. undefined = too late to offer. */
export function offerExpiry(nowMs: number, slotStartMs: number): IsoUtc | undefined {
  const at = Math.min(nowMs + OFFER_MINUTES * MINUTE, slotStartMs - OFFER_CUTOFF_HOURS * HOUR);
  return at > nowMs ? toIso(at) : undefined;
}

export const slotKeyOf = (staffId: Id, startIso: IsoUtc): string => `${staffId}@${startIso}`;

export type AcceptDecision = 'accept' | 'taken' | 'expired' | 'already_accepted' | 'invalid';

/**
 * First accept wins. `siblings` are the other offers for the same slot. The caller runs this inside
 * one storage transaction, so two simultaneous accepts see each other's write.
 */
export function decideAccept(offer: WaitlistOffer, siblings: WaitlistOffer[], nowMs: number, slotIsFree: boolean): AcceptDecision {
  if (offer.status === 'accepted') return 'already_accepted';
  if (offer.status === 'taken' || siblings.some((s) => s.status === 'accepted')) return 'taken';
  if (offer.status === 'expired' || offer.status === 'withdrawn' || nowMs >= toMs(offer.expiresAt)) return 'expired';
  return slotIsFree ? 'accept' : 'taken';
}

/** Open offers that have run out. */
export function expiredOffers(offers: WaitlistOffer[], nowMs: number): WaitlistOffer[] {
  return offers.filter((o) => o.status === 'open' && nowMs >= toMs(o.expiresAt));
}

/** Days still in the future for an entry; an entry with none left is stale. */
export function entryIsStale(entry: WaitlistEntry, today: DayKey): boolean {
  return entry.days.every((d) => d < today);
}
